<div class="w-full overflow-hidden p-6">
  <!-- ID -->
  <div class="mb-6">
    <label class="mb-1 block text-sm font-semibold text-gray-700">ID</label>
    <button
      [dojoCopyToClipboard]="data().id"
      class="group inline-flex cursor-pointer items-center gap-1.5 font-mono text-sm text-gray-500 hover:text-gray-700"
    >
      {{ data().id }}
      <dojo-icon icon="copy" [size]="'xs'" class="opacity-40 group-hover:opacity-100"></dojo-icon>
    </button>
  </div>

  <!-- Transformation Flow -->
  <div class="relative w-full">
    <div class="space-y-0">
      <!-- Step 1: Input -->
      <div class="relative pb-6">
        <!-- Connector Line -->
        <div class="absolute bottom-0 left-4 top-6 w-px bg-gray-200"></div>
        <!-- Step Indicator -->
        <div class="flex items-start gap-3">
          <div
            class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
          >
            <dojo-icon icon="cube" [size]="'xs'"></dojo-icon>
          </div>
          <div class="min-w-0 flex-1 pt-0.5">
            <!-- Single representation: link at title level -->
            @if (hasSingleInputRepresentation()) {
              <button
                (click)="navigateToNode.emit(singleInputRepresentation().id)"
                class="text-sm font-medium text-gray-900 transition-colors hover:text-blue-600"
              >
                Input
                <dojo-icon icon="arrow-up-right-from-square" [size]="'xs'" class="ml-1 opacity-50"></dojo-icon>
              </button>

              <!-- Schema -->
              @if (singleInputRepresentation().representation?.model) {
                <div class="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                  <div class="space-y-2">
                    @for (field of singleInputRepresentation().fields; track field.key) {
                      <div
                        class="flex items-start justify-between gap-2"
                        [ngClass]="{ 'opacity-40': !field.isUsed }"
                        [matTooltip]="field.isUsed ? '' : 'This field is inputted but not required by this procedure'"
                        [matTooltipPosition]="'left'"
                      >
                        <div class="min-w-0 flex-1">
                          <span
                            class="flex items-center gap-1 text-xs"
                            [ngClass]="field.isUsed ? 'text-gray-700' : 'text-gray-400'"
                          >
                            @if (field.def.label_type === 'latex') {
                              <span class="inline-block rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">
                                <markdown
                                  [data]="'$' + (field.def.label ?? field.key) + '$'"
                                  [katex]="true"
                                  [katexOptions]="katexOptions"
                                ></markdown>
                              </span>
                            } @else {
                              <code class="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700">{{
                                field.def.label ?? field.key
                              }}</code>
                            }
                            @if (field.def['optional']) {
                              <span class="font-normal text-gray-400">(optional)</span>
                            }
                          </span>
                          @if (field.def.description) {
                            <div class="mt-0.5 text-xs" [ngClass]="field.isUsed ? 'text-gray-400' : 'text-gray-300'">
                              <markdown
                                [data]="field.def.description"
                                [katex]="true"
                                [katexOptions]="katexOptions"
                              ></markdown>
                            </div>
                          }
                        </div>
                        <exam-type-badge [field]="field.def" [inactive]="!field.isUsed"></exam-type-badge>
                      </div>
                    }
                  </div>
                </div>
              }
            } @else {
              <!-- Multiple representations: group by representation with individual links -->
              <span class="text-sm font-medium text-gray-900">Input</span>

              <!-- Input Representations -->
              <div class="mt-3 space-y-3">
                @for (inputRep of inputRepresentations(); track inputRep.id) {
                  <div class="rounded-lg border border-gray-200 bg-white">
                    <!-- Header with link -->
                    <button
                      (click)="navigateToNode.emit(inputRep.id)"
                      class="flex w-full flex-col text-left transition-colors hover:bg-gray-50"
                    >
                      <div class="flex w-full items-center gap-2 px-3 py-2">
                        <svg
                          class="h-4 w-4 shrink-0 text-gray-500"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path [attr.d]="inputRep.svgPath" />
                        </svg>
                        <span class="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{{
                          inputRep.name
                        }}</span>
                        <dojo-icon
                          icon="arrow-up-right-from-square"
                          [size]="'xs'"
                          class="shrink-0 opacity-40"
                        ></dojo-icon>
                      </div>
                      <div class="mx-3 border-b border-gray-200"></div>
                    </button>
                    <!-- Schema -->
                    @if (inputRep.representation?.model) {
                      <div class="p-3">
                        <div class="space-y-2">
                          @for (field of inputRep.fields; track field.key) {
                            <div
                              class="flex items-start justify-between gap-2"
                              [ngClass]="{ 'opacity-40': !field.isUsed }"
                              [matTooltip]="
                                field.isUsed ? '' : 'This field is inputted but not required by this procedure'
                              "
                              [matTooltipPosition]="'left'"
                            >
                              <div class="min-w-0 flex-1">
                                <span
                                  class="flex items-center gap-1 text-xs"
                                  [ngClass]="field.isUsed ? 'text-gray-700' : 'text-gray-400'"
                                >
                                  @if (field.def.label_type === 'latex') {
                                    <span class="inline-block rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">
                                      <markdown
                                        [data]="'$' + (field.def.label ?? field.key) + '$'"
                                        [katex]="true"
                                        [katexOptions]="katexOptions"
                                      ></markdown>
                                    </span>
                                  } @else {
                                    <code class="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700">{{
                                      field.def.label ?? field.key
                                    }}</code>
                                  }
                                  @if (field.def['optional']) {
                                    <span class="font-normal text-gray-400">(optional)</span>
                                  }
                                </span>
                                @if (field.def.description) {
                                  <div
                                    class="mt-0.5 text-xs"
                                    [ngClass]="field.isUsed ? 'text-gray-400' : 'text-gray-300'"
                                  >
                                    <markdown
                                      [data]="field.def.description"
                                      [katex]="true"
                                      [katexOptions]="katexOptions"
                                    ></markdown>
                                  </div>
                                }
                              </div>
                              <exam-type-badge [field]="field.def" [inactive]="!field.isUsed"></exam-type-badge>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Step 2: Transformation -->
      <div class="relative pb-6">
        <!-- Connector Line -->
        <div class="absolute bottom-0 left-4 top-6 w-px bg-gray-200"></div>
        <!-- Step Indicator -->
        <div class="flex items-start gap-3">
          <div
            class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
          >
            <dojo-icon icon="gears" [size]="'xs'"></dojo-icon>
          </div>
          <div class="min-w-0 flex-1 pt-0.5">
            <span class="text-sm font-medium text-gray-900">Transformation</span>

            <!-- Formula -->
            @if (data().formula_latex) {
              <div class="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div class="flex items-center justify-center gap-2">
                  <span class="text-sm text-gray-500">Formula:</span>
                  <markdown
                    [data]="'$' + data().formula_latex + '$'"
                    [katex]="true"
                    [katexOptions]="katexOptions"
                  ></markdown>
                </div>
              </div>
            }

            <!-- Implementation -->
            <div class="mt-3">
              <div class="mb-2 flex items-center justify-between">
                <span class="text-xs font-medium text-gray-500">Implementation</span>
                <!-- TODO: Re-enable when AST analysis is moved to backend API
                <button
                  (click)="analyzeDependencies()"
                  [disabled]="isAnalyzingDependencies()"
                  class="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
                  matTooltip="Analyze dependencies using TypeScript AST"
                >
                  <dojo-icon [icon]="isAnalyzingDependencies() ? 'spinner' : 'code'" [size]="'xs'"></dojo-icon>
                  <span>Analyze</span>
                </button>
                -->
              </div>
              <div class="w-full overflow-hidden rounded-lg border border-gray-200" style="height: 200px">
                @if (isBrowser) {
                  <ngx-monaco-editor
                    style="height: 200px; width: 100%"
                    [options]="editorOptions"
                    [model]="editorModel()"
                  />
                } @else {
                  <pre class="h-full w-full overflow-auto bg-gray-50 p-3 text-xs">{{ editorModel().value }}</pre>
                }
              </div>

              <!-- Dependencies Analysis Results -->
              @if (dependencyError()) {
                <div class="mt-2 rounded-lg border border-red-200 bg-red-50 p-3">
                  <div class="flex items-center gap-2 text-sm text-red-700">
                    <dojo-icon icon="circle-exclamation" [size]="'xs'"></dojo-icon>
                    <span>{{ dependencyError() }}</span>
                  </div>
                </div>
              }

              @if (dependencies() !== null) {
                <div class="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <!-- Primitive Operations -->
                  @if (dependencies()!.length > 0) {
                    <div class="mb-2 text-xs font-medium text-gray-500">Primitive Operations</div>
                    <div class="flex flex-wrap gap-1.5">
                      @for (dep of dependencies(); track dep.operationId; let i = $index) {
                        <button
                          [matMenuTriggerFor]="opMenu"
                          class="inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-all hover:ring-2 hover:ring-gray-300"
                          [ngClass]="dep.categoryClasses"
                        >
                          <markdown
                            class="inline-block"
                            [data]="'$' + dep.symbol + '$'"
                            [katex]="true"
                            [katexOptions]="katexOptions"
                          ></markdown>
                          <span>{{ dep.name }}</span>
                          @if (dep.count > 1) {
                            <span
                              class="inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
                              [ngClass]="dep.countBadgeClasses"
                            >
                              ×{{ dep.count }}
                            </span>
                          }
                        </button>
                        <mat-menu #opMenu="matMenu" class="no-width-constraints">
                          <div class="w-72 p-3" (click)="$event.stopPropagation()">
                            <!-- Header -->
                            <div class="mb-3 flex items-center gap-2">
                              <span
                                class="inline-flex h-10 w-10 items-center justify-center rounded-lg text-lg"
                                [ngClass]="dep.categoryClasses"
                              >
                                <markdown
                                  [data]="'$' + dep.symbol + '$'"
                                  [katex]="true"
                                  [katexOptions]="katexOptions"
                                ></markdown>
                              </span>
                              <div>
                                <div class="font-semibold text-gray-900">{{ dep.name }}</div>
                                <div class="text-xs text-gray-500">{{ dep.category | titlecase }}</div>
                              </div>
                            </div>

                            <!-- Description -->
                            @if (dep.description) {
                              <p class="mb-3 text-sm text-gray-600">{{ dep.description }}</p>
                            }

                            <!-- Details -->
                            <div class="space-y-2 border-t border-gray-100 pt-3">
                              <!-- ID -->
                              <div class="flex items-center justify-between text-xs">
                                <span class="text-gray-500">ID</span>
                                <code class="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{{
                                  dep.operationId
                                }}</code>
                              </div>

                              <!-- Arity -->
                              <div class="flex items-center justify-between text-xs">
                                <span class="text-gray-500">Arity</span>
                                <span class="text-gray-700">{{ dep.arityLabel }}</span>
                              </div>

                              <!-- Usage Count -->
                              <div class="flex items-center justify-between text-xs">
                                <span class="text-gray-500">Used in this procedure</span>
                                <span
                                  class="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 font-semibold"
                                  [ngClass]="dep.countBadgeClasses"
                                >
                                  {{ dep.count }}×
                                </span>
                              </div>
                            </div>

                            <!-- Example -->
                            @if (dep.example) {
                              <div class="mt-3 border-t border-gray-100 pt-3">
                                <div class="mb-1 text-xs font-medium text-gray-500">Example</div>
                                <div class="rounded bg-gray-100 px-2 py-1.5 text-sm">
                                  <markdown
                                    [data]="dep.example"
                                    [katex]="true"
                                    [katexOptions]="katexOptions"
                                  ></markdown>
                                </div>
                              </div>
                            }
                          </div>
                        </mat-menu>
                      }
                    </div>
                  } @else {
                    <div class="text-xs text-gray-400">No primitive operations detected</div>
                  }

                  <!-- External Functions -->
                  @if (externalFunctions().length > 0) {
                    <div class="mt-3 border-t border-gray-200 pt-3">
                      <div class="mb-2 text-xs font-medium text-gray-500">External Functions</div>
                      <div class="flex flex-wrap gap-1.5">
                        @for (func of externalFunctions(); track func) {
                          <code class="rounded bg-rose-100 px-1.5 py-0.5 text-xs text-rose-700">{{ func }}()</code>
                        }
                      </div>
                    </div>
                  }

                  <!-- External Constants -->
                  @if (externalConstants().length > 0) {
                    <div class="mt-3 border-t border-gray-200 pt-3">
                      <div class="mb-2 text-xs font-medium text-gray-500">External Constants</div>
                      <div class="flex flex-wrap gap-1.5">
                        @for (constant of externalConstants(); track constant) {
                          <code class="rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-700">{{
                            constant
                          }}</code>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Output -->
      <div class="relative">
        <!-- Step Indicator -->
        <div class="flex items-start gap-3">
          <div
            class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
          >
            <dojo-icon icon="equals" [size]="'xs'"></dojo-icon>
          </div>
          <div class="min-w-0 flex-1 pt-0.5">
            <span class="text-sm font-medium text-gray-900">Output</span>

            <!-- Output Representation -->
            <div class="mt-3">
              <div class="rounded-lg border border-gray-200 bg-white">
                <!-- Header with link -->
                <button
                  (click)="navigateToNode.emit(outputRepresentation().id)"
                  class="flex w-full flex-col text-left transition-colors hover:bg-gray-50"
                >
                  <div class="flex w-full items-center gap-2 px-3 py-2">
                    <svg
                      class="h-4 w-4 shrink-0 text-gray-500"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path [attr.d]="outputRepresentation().svgPath" />
                    </svg>
                    <span class="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{{
                      outputRepresentation().name
                    }}</span>
                    <dojo-icon icon="arrow-up-right-from-square" [size]="'xs'" class="shrink-0 opacity-40"></dojo-icon>
                  </div>
                  <div class="mx-3 border-b border-gray-200"></div>
                </button>
                <!-- Schema -->
                @if (outputRepresentation().representation?.model) {
                  <div class="p-3">
                    <div class="space-y-2">
                      @for (field of outputRepresentation().fields; track field.key) {
                        <div class="flex items-start justify-between gap-2">
                          <div class="min-w-0 flex-1">
                            <span class="flex items-center gap-1 text-xs text-gray-700">
                              @if (field.def.label_type === 'latex') {
                                <span class="inline-block rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">
                                  <markdown
                                    [data]="'$' + (field.def.label ?? field.key) + '$'"
                                    [katex]="true"
                                    [katexOptions]="katexOptions"
                                  ></markdown>
                                </span>
                              } @else {
                                <code class="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700">{{
                                  field.def.label ?? field.key
                                }}</code>
                              }
                              @if (field.def['optional']) {
                                <span class="font-normal text-gray-400">(optional)</span>
                              }
                            </span>
                            @if (field.def.description) {
                              <div class="mt-0.5 text-xs text-gray-400">
                                <markdown
                                  [data]="field.def.description"
                                  [katex]="true"
                                  [katexOptions]="katexOptions"
                                ></markdown>
                              </div>
                            }
                          </div>
                          <exam-type-badge [field]="field.def"></exam-type-badge>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Example Section -->
  <div class="mt-8 border-t border-gray-200 pt-6">
    <label class="mb-4 block text-sm font-semibold text-gray-700">Example Playground</label>
    <div class="relative w-full">
      <div class="space-y-0">
        <!-- Example Input -->
        <div class="relative pb-6">
          <!-- Connector Line -->
          <div class="absolute bottom-0 left-4 top-6 w-px bg-gray-200"></div>
          <!-- Step Indicator -->
          <div class="flex items-start gap-3">
            <div
              class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
            >
              <dojo-icon icon="cube" [size]="'xs'"></dojo-icon>
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <!-- Single representation: link at title level -->
              @if (hasSingleInputRepresentation()) {
                <button
                  (click)="navigateToNode.emit(singleInputRepresentation().id)"
                  class="text-sm font-medium text-gray-900 transition-colors hover:text-blue-600"
                >
                  Input
                  <dojo-icon icon="arrow-up-right-from-square" [size]="'xs'" class="ml-1 opacity-50"></dojo-icon>
                </button>

                <!-- Input Fields -->
                @if (hasUsedFields(singleInputRepresentation())) {
                  <div class="mt-3 rounded-lg border border-gray-200 bg-gray-50/30 p-3">
                    <div class="space-y-2">
                      @for (field of singleInputRepresentation().fields; track field.key) {
                        @if (field.isUsed) {
                          <div class="flex items-center gap-3">
                            <label class="flex w-20 shrink-0 items-center gap-1 text-xs text-gray-600">
                              @if (field.def.label_type === 'latex') {
                                <span class="inline-block rounded bg-gray-200 px-1 py-0.5 text-gray-700">
                                  <markdown
                                    [data]="'$' + (field.def.label ?? field.key) + '$'"
                                    [katex]="true"
                                    [katexOptions]="katexOptions"
                                  ></markdown>
                                </span>
                              } @else {
                                <code class="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs text-gray-700">{{
                                  field.def.label ?? field.key
                                }}</code>
                              }
                              @if (field.def['optional']) {
                                <span class="font-normal text-gray-400">(opt)</span>
                              }
                            </label>
                            <div class="min-w-0 flex-1">
                              <ng-container
                                *ngTemplateOutlet="
                                  fieldInputTemplate;
                                  context: { field: field, repIndex: 0, inputRep: singleInputRepresentation() }
                                "
                              ></ng-container>
                            </div>
                          </div>
                        }
                      }
                    </div>
                  </div>
                }
              } @else {
                <!-- Multiple representations: group by representation with links -->
                <span class="text-sm font-medium text-gray-900">Input</span>

                <!-- Input Fields for each representation -->
                <div class="mt-3 space-y-3">
                  @for (inputRep of inputRepresentations(); track inputRep.id; let repIndex = $index) {
                    @if (hasUsedFields(inputRep)) {
                      <div class="rounded-lg border border-gray-200 bg-gray-50/30">
                        <button
                          (click)="navigateToNode.emit(inputRep.id)"
                          class="flex w-full items-center justify-between border-b border-gray-200 px-3 py-2 text-left transition-colors hover:bg-gray-100"
                        >
                          <span class="text-xs font-medium text-gray-700">{{ inputRep.name }}</span>
                          <dojo-icon icon="arrow-up-right-from-square" [size]="'xs'" class="opacity-40"></dojo-icon>
                        </button>
                        <div class="space-y-2 p-3">
                          @for (field of inputRep.fields; track field.key) {
                            @if (field.isUsed) {
                              <div class="flex items-center gap-3">
                                <label class="flex w-20 shrink-0 items-center gap-1 text-xs text-gray-600">
                                  @if (field.def.label_type === 'latex') {
                                    <span class="inline-block rounded bg-gray-200 px-1 py-0.5 text-gray-700">
                                      <markdown
                                        [data]="'$' + (field.def.label ?? field.key) + '$'"
                                        [katex]="true"
                                        [katexOptions]="katexOptions"
                                      ></markdown>
                                    </span>
                                  } @else {
                                    <code class="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs text-gray-700">{{
                                      field.def.label ?? field.key
                                    }}</code>
                                  }
                                  @if (field.def['optional']) {
                                    <span class="font-normal text-gray-400">(opt)</span>
                                  }
                                </label>
                                <div class="min-w-0 flex-1">
                                  <ng-container
                                    *ngTemplateOutlet="
                                      fieldInputTemplate;
                                      context: { field: field, repIndex: repIndex, inputRep: inputRep }
                                    "
                                  ></ng-container>
                                </div>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    }
                  }
                </div>
              }

              <!-- Execute Button -->
              <div class="mt-4">
                <dojo-button [size]="'s'" [fill]="'solid'" (click)="executeExample()">
                  <dojo-icon icon="play" [size]="'xs'" class="mr-2"></dojo-icon>
                  Execute
                </dojo-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Example Output -->
        <div class="relative">
          <!-- Step Indicator -->
          <div class="flex items-start gap-3">
            <div
              class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              [ngClass]="exampleOutput() ? 'bg-gray-100 text-gray-500' : 'bg-gray-100 text-gray-400'"
            >
              <dojo-icon icon="equals" [size]="'xs'"></dojo-icon>
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <span class="text-sm font-medium text-gray-900">Output</span>

              @if (exampleOutput(); as output) {
                <!-- Primary Result Display -->
                <div class="mt-2 rounded-lg border border-green-500 bg-green-50 p-3">
                  <div class="text-md font-light text-gray-900">
                    <markdown [data]="renderedOutputTemplate()" [katex]="true" [katexOptions]="katexOptions"></markdown>
                  </div>
                </div>
                <!-- Breakdown with connection lines -->
                <div class="relative mt-4 pl-7">
                  <!-- Vertical connector line -->
                  <div class="absolute -top-4 bottom-0 left-[13px] w-px bg-gray-300"></div>
                  <div class="space-y-2">
                    @for (field of outputRepresentation().fields; track field.key; let last = $last) {
                      <div
                        class="relative flex items-stretch [&:last-child_.line-blocker]:block"
                        [class.last-item]="last"
                      >
                        <!-- White blocker to hide bottom half of line for last item -->
                        <div class="line-blocker absolute -left-[16px] bottom-0 top-1/2 hidden w-[4px] bg-white"></div>
                        <!-- Content box with horizontal connector -->
                        <div
                          class="relative flex-1 rounded-md border p-3 before:absolute before:-left-[15px] before:top-1/2 before:h-px before:w-[15px] before:-translate-y-1/2 before:bg-gray-300"
                          [class.border-gray-300]="!field.def['optional']"
                          [class.bg-gray-100]="!field.def['optional']"
                          [class.border-gray-100]="field.def['optional']"
                          [class.bg-gray-50]="field.def['optional']"
                        >
                          <div class="flex items-start justify-between gap-2">
                            <div class="min-w-0 flex-1">
                              <span
                                class="flex items-center gap-1.5 text-sm"
                                [class.text-gray-900]="!field.def['optional']"
                                [class.text-gray-700]="field.def['optional']"
                              >
                                @if (field.def.label_type === 'latex') {
                                  <span class="inline-block rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">
                                    <markdown
                                      [data]="'$' + (field.def.label ?? field.key) + '$'"
                                      [katex]="true"
                                      [katexOptions]="katexOptions"
                                    ></markdown>
                                  </span>
                                } @else {
                                  <code class="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700">{{
                                    field.def.label ?? field.key
                                  }}</code>
                                }
                                @if (field.def['optional']) {
                                  <span class="text-xs font-normal text-gray-400">(optional)</span>
                                }
                              </span>
                              @if (field.def.description) {
                                <div class="mt-0.5 text-xs text-gray-500">
                                  <markdown
                                    [data]="field.def.description"
                                    [katex]="true"
                                    [katexOptions]="katexOptions"
                                  ></markdown>
                                </div>
                              }
                            </div>
                            <div class="max-w-[50%] break-words text-right">
                              @if (field.def.type === 'enum') {
                                <span
                                  class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
                                  [ngClass]="fieldTypeService.getTypeBadgeClasses(field.def)"
                                >
                                  {{ output[field.key] }}
                                </span>
                              } @else {
                                <span class="text-sm text-gray-900">
                                  <markdown
                                    [data]="formattedOutputValues().get(field.key) ?? ''"
                                    [katex]="true"
                                    [katexOptions]="katexOptions"
                                  ></markdown>
                                </span>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              } @else {
                <div class="mt-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
                  <span class="text-xs text-gray-400">Enter values and click Execute to see the result</span>
                </div>
              }

              @if (exampleError()) {
                <div class="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                  <span class="text-xs text-red-600">{{ exampleError() }}</span>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Template for field inputs to avoid duplication -->
<ng-template #fieldInputTemplate let-field="field" let-repIndex="repIndex" let-inputRep="inputRep">
  @if (field.def.type === 'number') {
    <dojo-number-input
      [formCtrl]="getExampleFormControl(repIndex, field.key)"
      size="s"
      [min]="field.def['min'] ?? null"
      [max]="field.def['max'] ?? null"
      placeholder="Enter value..."
    ></dojo-number-input>
  } @else if (field.def.type === 'string') {
    <dojo-text-input
      [formCtrl]="getExampleFormControl(repIndex, field.key)"
      size="s"
      placeholder="Enter value..."
    ></dojo-text-input>
    @if (fieldTypeService.isLatexField(field.def) && getExampleFormControl(repIndex, field.key).value) {
      <div class="mt-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm">
        <markdown
          [data]="'$' + getExampleFormControl(repIndex, field.key).value + '$'"
          [katex]="true"
          [katexOptions]="katexOptions"
        ></markdown>
      </div>
    }
  } @else if (field.def.type === 'enum') {
    <dojo-single-select
      [formCtrl]="getExampleFormControl(repIndex, field.key)"
      size="s"
      [options]="getEnumOptions(inputRep.id, field.key)"
      optionLabel="label"
      optionValue="value"
      placeholder="Select..."
    ></dojo-single-select>
  } @else if (field.def.type === 'array') {
    <div class="space-y-2">
      @for (item of getArrayFormArray(repIndex, field.key).controls; track $index; let i = $index) {
        <div class="flex items-start gap-2">
          <div class="min-w-0 flex-1">
            @if (isNumberArrayField(field.def)) {
              <dojo-number-input
                [formCtrl]="getArrayItemControl(repIndex, field.key, i)"
                size="s"
                placeholder="Enter number..."
              ></dojo-number-input>
            } @else {
              <dojo-text-input
                [formCtrl]="getArrayItemControl(repIndex, field.key, i)"
                size="s"
                [placeholder]="isLatexArrayField(field.def) ? 'Enter LaTeX...' : 'Enter value...'"
              ></dojo-text-input>
            }
            @if (isLatexArrayField(field.def) && getArrayItemControl(repIndex, field.key, i).value) {
              <div class="mt-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm">
                <markdown
                  [data]="'$' + getArrayItemControl(repIndex, field.key, i).value + '$'"
                  [katex]="true"
                  [katexOptions]="katexOptions"
                ></markdown>
              </div>
            }
            @if (getArrayItemControl(repIndex, field.key, i).errors) {
              <div class="mt-1 text-xs text-red-500">
                @if (getArrayItemControl(repIndex, field.key, i).errors?.['latex']) {
                  {{ getArrayItemControl(repIndex, field.key, i).errors?.['latex'] }}
                } @else if (getArrayItemControl(repIndex, field.key, i).errors?.['pattern']) {
                  Invalid number format
                }
              </div>
            }
          </div>
          @if (getArrayFormArray(repIndex, field.key).length > 1) {
            <dojo-icon-button
              icon="trash"
              [size]="'xs'"
              iconClass="text-red-500"
              (click)="removeArrayItem(repIndex, field.key, i)"
            ></dojo-icon-button>
          }
        </div>
      }
      <dojo-button
        [size]="'xs'"
        [fill]="'outline'"
        class="w-[100px]"
        (click)="addArrayItem(repIndex, field.key, field.def)"
      >
        <dojo-icon icon="plus" [size]="'xs'" class="mr-1"></dojo-icon>
        <span>Add item</span>
      </dojo-button>
    </div>
  }
</ng-template>
